/**
 * NOTIF-003 — the SPA side of the registry's page keys (config/notifications.php `pages`):
 * which route a counter belongs to and what to call the level / module / page in the UI.
 */
import type { NotificationLevel } from '@/services/notificationsApi'

export interface NotificationPage {
  route: string
  label: string
  /** The registry module the page belongs to (NOTIF-007: the page filter's "Module · Page" labels). */
  module: string
}

/** page_key → the sidebar entry it counts for (labels as the sidebar spells them). */
export const NOTIFICATION_PAGES: Record<string, NotificationPage> = {
  'dd.queue': { route: '/design-development/queue', label: 'Queue', module: 'design_development' },
  'dd.workload': {
    route: '/design-development/workload',
    label: 'Workload',
    module: 'design_development'
  },
  'dd.dashboard': {
    route: '/design-development/dashboard',
    label: 'D&D Dashboard',
    module: 'design_development'
  },
  'dd.history': {
    route: '/design-development/history',
    label: 'History',
    module: 'design_development'
  },
  'dd.cancelled': {
    route: '/design-development/cancelled',
    label: 'Cancelled',
    module: 'design_development'
  },
  // NOTIF-006 — the submitter's side (module `sales`): his own record page.
  'sales.submitted': {
    route: '/sales/workflow/submitted',
    label: 'Submitted Requests',
    module: 'sales'
  }
}

/** The full notifications page (NOTIF-007); the bell's footer appears once this route exists. */
export const NOTIFICATIONS_PAGE_PATH = '/notifications'

/** The settings page (NOTIF-004) — the panel's gear and the Settings sidebar lead here. */
export const NOTIFICATION_SETTINGS_PATH = '/settings/notifications'

/** Registry modules (config/notifications.php `modules`) → the label in a row's meta line. */
export const MODULE_LABELS: Record<string, string> = {
  system: 'RUKH',
  design_development: 'Design & Development',
  sales: 'Sales'
}

export const LEVEL_LABELS: Record<NotificationLevel, string> = {
  action_needed: 'Action needed',
  heads_up: 'Heads-up',
  info: 'Info'
}

/**
 * Levels that count as read once seen — the cursor rested on the row, the row has focus, or on
 * a touch device the row is in view (decision 2, amended 2026-10-21). action_needed only by click.
 */
export const LEVELS_READ_ON_SEEN: NotificationLevel[] = ['heads_up', 'info']

export function pageRoute(pageKey: string | null | undefined): string | null {
  return pageKey ? (NOTIFICATION_PAGES[pageKey]?.route ?? null) : null
}

export function pageLabel(pageKey: string | null | undefined): string | null {
  return pageKey ? (NOTIFICATION_PAGES[pageKey]?.label ?? pageKey) : null
}

export function moduleLabel(module: string): string {
  return MODULE_LABELS[module] ?? module
}

/** "Design & Development · Queue", or just the module when the row has no page. */
export function metaLabel(module: string, pageKey: string | null | undefined): string {
  const page = pageLabel(pageKey)
  return page ? `${moduleLabel(module)} · ${page}` : moduleLabel(module)
}

/** The bell badge text: 0 → '', 1–99 → the number, more → '99+'. */
export function badgeLabel(count: number): string {
  if (!Number.isFinite(count) || count <= 0) return ''
  return count > 99 ? '99+' : String(Math.floor(count))
}
